import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import Messages from './Messages';

function App() {
  const [page, setPage] = useState('intro'); // 'intro', 'question', 'success', 'messages'
  const [noCount, setNoCount] = useState(0);
  
  // No Button Position State - stores target position
  const [noPos, setNoPos] = useState({ x: 50, y: 50 });
  const [isMoved, setIsMoved] = useState(false);
  const [animationKey, setAnimationKey] = useState(0); // Force new animation on each move
  const noBtnRef = useRef(null);

  // Rotating images for question page
  const questionImages = ['/IMG_1371.jpeg'];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (page === 'question') {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % questionImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [page, questionImages.length]);

  // Countdown to Friday 9pm
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  // Easter egg
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const [showMessagesBtn, setShowMessagesBtn] = useState(false);
  
  useEffect(() => {
    // Friday Feb 13, 2026 at 9:00 PM
    const targetDate = new Date('2026-02-13T21:00:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleStart = () => {
    setPage('question');
  };

  const handleBack = () => {
    setPage('intro');
    setNoCount(0);
    setIsMoved(false);
    setAnimationKey(0);
  };

  const handleYesClick = () => {
    setPage('success');
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#ff69b4', '#ff1493', '#ffb6c1'] // Pink themed confetti
    });
  };

  // Continuous confetti for success page
  useEffect(() => {
    if (page === 'success') {
      const interval = setInterval(() => {
        confetti({
          particleCount: 30,
          spread: 50,
          origin: { y: 0.6 },
          colors: ['#ff69b4', '#ff1493', '#ffb6c1'],
          startVelocity: 20,
          gravity: 0.8,
          ticks: 100
        });
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [page]);

  const [hearts, setHearts] = useState([]);

  useEffect(() => {
    const heartTypes = ['❤️', '💕', '♥️', '🍑', '🫐', '🧁'];
    const newHearts = [];
    for (let i = 0; i < 30; i++) {
      newHearts.push({
        id: i,
        left: Math.random() * 100,
        animationDuration: Math.random() * 8 + 6, // 6-14 seconds to fall
        animationDelay: Math.random() * 15,
        swayDuration: Math.random() * 2 + 2, // 2-4 seconds per sway
        opacity: Math.random() * 0.4 + 0.4, // 0.4-0.8 opacity
        size: Math.random() * 20 + 14, // 14-34px
        heart: heartTypes[Math.floor(Math.random() * heartTypes.length)],
        swayDirection: Math.random() > 0.5 ? 'sway-left' : 'sway-right'
      });
    }
    setHearts(newHearts);
  }, []);

  const moveNoButton = () => {
    if (!isMoved) setIsMoved(true);

    const maxBtnWidth = 280;
    const maxBtnHeight = 60;

    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    const margin = 20;
    const minX = margin;
    const minY = margin;
    const maxX = Math.max(margin, viewportWidth - maxBtnWidth - margin);
    const maxY = Math.max(margin, viewportHeight - maxBtnHeight - margin);

    // Generate random target coordinates
    let newX = Math.random() * (maxX - minX) + minX;
    let newY = Math.random() * (maxY - minY) + minY;

    // Ensure it doesn't overlap with the center "Yes" area
    const centerX = viewportWidth / 2;
    const centerY = viewportHeight / 2;
    const btnCenterX = newX + maxBtnWidth / 2;
    const btnCenterY = newY + maxBtnHeight / 2;
    const distanceX = Math.abs(btnCenterX - centerX);
    const distanceY = Math.abs(btnCenterY - centerY);

    if (distanceX < 200 && distanceY < 150) {
      newX = newX < centerX ? minX : maxX;
      newY = newY < centerY ? minY : maxY;
    }

    // Final safety clamp
    newX = Math.max(0, Math.min(newX, viewportWidth - maxBtnWidth));
    newY = Math.max(0, Math.min(newY, viewportHeight - maxBtnHeight));

    setNoPos({ x: newX, y: newY });
    setAnimationKey(prev => prev + 1); // Trigger fresh animation
    setNoCount(noCount + 1);
  };

  const getNoButtonText = () => {
    const phrases = [
      "no",
      "humph.",
      "yarg.",
      "grrrrrrr.",
      "are you sewious :(",
      "GREG.",
      "i knew you didn't love me 😡",
      "pls don't leave me",
      "are you sure?",
      "pls reconsider",
      "don't leave me my hana",
      "yargrrr",
      "homph.",
      "you're making me sad",
      "if you hate me just say so",
      ":(((((((",
      "pls say yes!"
    ];
    return phrases[noCount % phrases.length];
  };

  // Calculate dynamic scale for Yes button (capped at 1.3)
  const yesScale = Math.min(1.3, 1 + noCount * 0.05);
  
  // Calculate pulse speed - gets faster with each No click
  // Starts at 2 seconds, gets faster down to 0.3 seconds
  const pulseDuration = Math.max(0.3, 2 - noCount * 0.15);

  return (
    <div className="App">
      <div className="hearts-bg">
        {hearts.map((h) => (
          <div
            key={h.id}
            className={`heart ${h.swayDirection}`}
            style={{
              left: `${h.left}%`,
              animationDuration: `${h.animationDuration}s, ${h.swayDuration}s`,
              animationDelay: `${h.animationDelay}s, ${h.animationDelay}s`,
              opacity: h.opacity,
              fontSize: `${h.size}px`
            }}
          >
            {h.heart}
          </div>
        ))}
      </div>
      
      {/* SEPARATE FLOATING CONTAINER FOR THE NO BUTTON */}
      {/* This ensures the coordinate system is always relative to the viewport (0,0 is top-left) */}
      {page === 'question' && isMoved && (
        <div className="floating-container">
          <motion.button
            className="no-button floating-no-button"
            ref={noBtnRef}
            animate={{ 
              top: noPos.y,
              left: noPos.x,
              rotate: animationKey * 360,
            }}
            transition={{ 
              top: {
                type: "spring",
                stiffness: 100,
                damping: 15,
                mass: 1
              },
              left: {
                type: "spring", 
                stiffness: 80,
                damping: 12,
                mass: 1.5
              },
              rotate: {
                type: "spring",
                stiffness: 80,
                damping: 15
              }
            }}
            onMouseEnter={moveNoButton}
            onClick={moveNoButton}
          >
            {getNoButtonText()}
          </motion.button>
        </div>
      )}

      <AnimatePresence mode="wait">
        
        {/* PAGE 1: INTRO */}
        {page === 'intro' && (
          <motion.div 
            key="intro"
            className="intro-card"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 1.2, opacity: 0, filter: "blur(10px)" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            whileHover={{ y: -5 }}
          >
            <div className="gif-container intro-gif">
              <img 
                src="/IMG_9715.jpg" 
                alt="Hana" 
              />
            </div>
            <h1 className="intro-title">Hi Hana!</h1>
            <p className="sub-text">I have a question for you...</p>
            <button className="start-button" onClick={handleStart}>
              Open 💌
            </button>
          </motion.div>
        )}

        {/* PAGE 2: QUESTION */}
        {page === 'question' && (
          <motion.div 
            key="question"
            className="container"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
          >
            <button className="back-button" onClick={handleBack}>
              ← Back
            </button>
            <div className="gif-container question-img">
              <motion.img 
                key={currentImageIndex}
                src={questionImages[currentImageIndex]}
                alt="Hana"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <div className="text-container">
              Hana, will you be my Valentine?
            </div>
            <div className="button-container">
              <motion.button
                className="yes-button"
                onClick={handleYesClick}
                animate={{ 
                  scale: [yesScale, yesScale * 1.1, yesScale],
                }}
                transition={{
                  duration: pulseDuration,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                whileHover={{ scale: yesScale * 1.15 }}
                whileTap={{ scale: yesScale * 0.9 }}
                style={{
                  '--glow-duration': `${pulseDuration}s`
                }}
              >
                Yes
              </motion.button>

              {/* Only render the static No button if it hasn't moved yet */}
              {!isMoved && (
                <button
                  className="no-button"
                  onMouseEnter={moveNoButton}
                  onClick={moveNoButton}
                >
                  No
                </button>
              )}
            </div>
          </motion.div>
        )}

        {/* PAGE 3: SUCCESS */}
        {page === 'success' && (
          <motion.div 
            key="success"
            className="success-card"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <button className="back-button" onClick={handleBack}>
              ← Back
            </button>
            <div className="gif-container success-img">
              <img 
                src="/IMG_0784.jpeg"
                alt="Us"
              />
            </div>
            <span 
              className="corner-emoji top-right"
              onClick={() => {}}
            >
              🎉
            </span>
            <span 
              className="corner-emoji bottom-left"
              onClick={() => setShowMessagesBtn(true)}
              style={{ cursor: 'pointer' }}
            >
              💕
            </span>
            <div className="celebration-text">WAHOO!!! I wuv you da most 😼</div>
            <p className="knew-it-text">i knew you'd say yes!!!!!!</p>
            
            {showEasterEgg && (
              <motion.div 
                className="easter-egg-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <button 
                  className="back-button easter-egg-back" 
                  onClick={() => setShowEasterEgg(false)}
                >
                  ← Back
                </button>
                <motion.div 
                  className="easter-egg-content"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  <div className="easter-egg-peach">🍑</div>
                  <p className="easter-egg-text">get ready for buttstuff</p>
                  <p className="easter-egg-subtext">i know you want it</p>
                </motion.div>
              </motion.div>
            )}
            
            <div className="countdown-container">
              <div className="countdown-timer">
                {countdown.days > 0 && (
                  <div className="countdown-item">
                    <span className="countdown-number">{countdown.days}</span>
                    <span className="countdown-unit">days</span>
                  </div>
                )}
                <div className="countdown-item">
                  <span className="countdown-number">{countdown.hours}</span>
                  <span className="countdown-unit">hrs</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">{countdown.minutes}</span>
                  <span className="countdown-unit">min</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">{countdown.seconds}</span>
                  <span className="countdown-unit">sec</span>
                </div>
              </div>
              <p className="countdown-label">until i see you again!</p>
            </div>

            {showMessagesBtn && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="messages-btn-wrapper"
              >
                <p className="favorite-activity-text">your favorite activity...</p>
                <button 
                  className="explore-messages-btn"
                  onClick={() => setPage('messages')}
                >
                  Explore Our Messages 💬
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {page === 'messages' && (
          <Messages onBack={() => setPage('success')} />
        )}


      </AnimatePresence>
    </div>
  );
}

export default App;
